import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BACKEND_URL } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CalendarDays, Check, CircleX, Clock, Video, MapPin, Phone, Link2, ChevronLeft, ChevronRight } from "lucide-react";
import Logo from "@/components/Logo";

// Anonymous funnel tracker — session id in sessionStorage, fires beacons non-blocking
const _sid = (() => {
  try {
    let s = sessionStorage.getItem("cdva_book_sid");
    if (!s) { s = Math.random().toString(36).slice(2) + Date.now().toString(36) + Math.random().toString(36).slice(2); sessionStorage.setItem("cdva_book_sid", s); }
    return s;
  } catch (_) { return "anon" + Math.random().toString(36).slice(2); }
})();
const _device = (() => {
  const w = window.innerWidth || 1024;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
})();
function track(event_type, ctx = {}) {
  try {
    const body = { session_id: _sid, event_type, device: _device,
      referrer: (document.referrer || "").split("?")[0].slice(0, 200), ...ctx };
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
      navigator.sendBeacon(`${BACKEND_URL}/api/public/booking/track`, blob);
    } else {
      fetch(`${BACKEND_URL}/api/public/booking/track`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body), keepalive: true,
      }).catch(() => {});
    }
  } catch (_) {}
}

async function pub(path, options = {}) {
  const res = await fetch(`${BACKEND_URL}/api${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try { const j = await res.json(); detail = j.detail || j.message || detail; } catch (_) {}
    throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
  }
  return res.json();
}

const LOC_ICON = { google_meet: Video, teams: Video, zoom_manual: Video, phone: Phone, in_person: MapPin, custom_link: Link2 };
const LOC_LABEL = { google_meet: "Google Meet", teams: "Microsoft Teams", zoom_manual: "Zoom", phone: "Phone call", in_person: "In person", custom_link: "Custom link" };

export default function BookingPublic() {
  const { orgSlug, aptSlug, userSlug } = useParams();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [ctx, setCtx] = useState(null);       // {org, appointment_types}
  const [apt, setApt] = useState(null);
  const [monthAnchor, setMonthAnchor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [daysMap, setDaysMap] = useState({});
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", notes: "" });
  const [answers, setAnswers] = useState({});
  const [confirmed, setConfirmed] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tz] = useState(() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (_) { return "UTC"; } });

  useEffect(() => {
    (async () => {
      try {
        if (aptSlug) {
          const r = await pub(`/public/booking/type/${orgSlug}/${aptSlug}${userSlug ? `?user_slug=${userSlug}` : ""}`);
          setCtx({ org: r.org });
          setApt(r.appointment_type);
          setStep(2);
          track("viewed", { org_slug: orgSlug, appointment_type_slug: aptSlug, user_slug: userSlug });
          track("type_selected", { org_slug: orgSlug, appointment_type_slug: aptSlug, user_slug: userSlug });
        } else {
          const r = await pub(`/public/booking/org/${orgSlug}`);
          setCtx(r);
          track("viewed", { org_slug: orgSlug, user_slug: userSlug });
          if ((r.appointment_types || []).length === 1) { setApt(r.appointment_types[0]); setStep(2); track("type_selected", { org_slug: orgSlug, appointment_type_slug: r.appointment_types[0].slug }); }
        }
      } catch (e) { setError(e.message); } finally { setLoading(false); }
    })();
  }, [orgSlug, aptSlug, userSlug]);

  // Load slots when apt/month changes
  useEffect(() => {
    if (!apt?.slug || !ctx?.org?.id) return;
    const start = new Date(monthAnchor);
    const end = new Date(monthAnchor); end.setMonth(end.getMonth() + 1); end.setDate(0);
    const params = new URLSearchParams({ from_date: start.toISOString().slice(0,10), to_date: end.toISOString().slice(0,10) });
    if (userSlug) params.set("user_slug", userSlug);
    pub(`/public/booking/slots/${orgSlug}/${apt.slug}?${params}`)
      .then((r) => setDaysMap(r.days || {}))
      .catch((e) => toast.error(e.message));
  }, [apt?.slug, monthAnchor, ctx?.org?.id, orgSlug, userSlug]);

  const daysInMonth = useMemo(() => {
    const first = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1);
    const startOffset = (first.getDay() + 6) % 7; // Mon=0
    const last = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 0);
    const arr = [];
    for (let i = 0; i < startOffset; i++) arr.push(null);
    for (let d = 1; d <= last.getDate(); d++) arr.push(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), d));
    return arr;
  }, [monthAnchor]);

  const slotsForDate = selectedDate ? (daysMap[selectedDate] || []) : [];

  const submit = async () => {
    if (!apt || !selectedSlot) return;
    if (!form.first_name || !form.last_name || !form.email) return toast.error("Please complete required fields");
    // Validate required custom questions
    for (const q of apt.custom_questions || []) {
      if (!q.required) continue;
      const v = answers[q.key || q.label];
      const empty = v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
      if (empty) return toast.error(`Please answer: ${q.label}`);
    }
    try {
      const params = userSlug ? `?user_slug=${userSlug}` : "";
      const r = await pub(`/public/booking/create/${orgSlug}/${apt.slug}${params}`, {
        method: "POST",
        body: JSON.stringify({
          starts_at: selectedSlot.starts_at, host_user_id: selectedSlot.host_user_id,
          first_name: form.first_name, last_name: form.last_name,
          email: form.email, phone: form.phone || null,
          notes: form.notes || "", answers,
        }),
      });
      setConfirmed(r);
      track("completed", { org_slug: orgSlug, appointment_type_slug: apt?.slug, user_slug: userSlug, booking_id: r.id });
      setStep(5);
    } catch (e) {
      // Handle the Teams-not-capable backend safety-net gracefully so the
      // client never sees a raw 409 / API detail. The backend already sends
      // a client-friendly message; make sure it reads naturally.
      const raw = (e && e.message) || "";
      if (raw.toLowerCase().includes("temporarily unavailable") || raw.includes("409")) {
        toast.error("Microsoft Teams isn't available for this appointment. Please choose another available meeting option or contact the provider.");
      } else {
        toast.error(raw || "Something went wrong. Please try again.");
      }
    }
  };

  if (loading) return <PubShell><p className="text-sm text-[#57534E] p-8">Loading…</p></PubShell>;
  if (error) return <PubShell><ErrorCard message={error} /></PubShell>;
  if (!ctx) return null;

  if (confirmed) {
    const Icon = LOC_ICON[confirmed.location_type] || Video;
    return (
      <PubShell org={ctx.org}>
        <div className="bg-white rounded-3xl border border-[#E8E2D9] p-8 text-center" data-testid="booking-confirmed">
          <div className="w-14 h-14 rounded-2xl bg-[#E8F0E4] flex items-center justify-center mx-auto mb-4">
            <Check size={22} className="text-[#2C3A29]" />
          </div>
          <h2 className="font-display font-bold text-2xl tracking-tight mb-2">You're booked in</h2>
          <p className="text-sm text-[#57534E]">Your <b>{confirmed.appointment_name}</b> is confirmed for:</p>
          <p className="text-base font-medium mt-2">{new Date(confirmed.starts_at).toLocaleString([], { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: tz })}</p>
          <p className="text-xs text-[#A8A29E] mt-1">Times shown in {tz}</p>
          {confirmed.meeting_url && (
            <a href={confirmed.meeting_url} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-2 mt-4 text-sm text-[#E8712C] font-medium">
              <Icon size={14} /> Join {LOC_LABEL[confirmed.location_type]}
            </a>
          )}
          <div className="mt-6 text-xs text-[#57534E]">
            Need to change?{" "}
            <a href={`/booking/manage/${confirmed.manage_token}`} className="underline text-[#E8712C]">
              Reschedule or cancel
            </a>
          </div>
        </div>
      </PubShell>
    );
  }

  return (
    <PubShell org={ctx.org}>
      {/* Step 1: choose type */}
      {step === 1 && ctx.appointment_types && (
        <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 md:p-8">
          <h2 className="font-display font-bold text-xl tracking-tight mb-1">Choose an appointment</h2>
          <p className="text-sm text-[#57534E] mb-5">Pick the meeting type that works best.</p>
          <div className="space-y-2">
            {ctx.appointment_types.map((t) => {
              const Icon = LOC_ICON[t.location_type] || Video;
              return (
                <button key={t.id} onClick={() => { setApt(t); setStep(2); }} data-testid={`pub-type-${t.slug}`}
                  className="w-full text-left border border-[#E8E2D9] rounded-2xl p-4 hover:border-[#E8712C]/40 transition-colors flex items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: t.color || "#E8712C" }} />
                      <span className="font-medium">{t.name}</span>
                    </div>
                    <div className="text-xs text-[#57534E] mt-1">{t.duration_minutes} min · <Icon size={11} className="inline" /> {LOC_LABEL[t.location_type]}</div>
                    {t.description && <p className="text-xs text-[#A8A29E] mt-1 line-clamp-2">{t.description}</p>}
                  </div>
                  <ChevronRight size={16} className="text-[#A8A29E]" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 2: pick date */}
      {step === 2 && apt && (
        <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 md:p-8">
          <div className="flex items-center justify-between mb-2">
            <button onClick={() => aptSlug ? null : setStep(1)} className="text-sm text-[#57534E] flex items-center gap-1" data-testid="pub-back-to-types">
              {!aptSlug && <><ChevronLeft size={14} /> Back</>}
            </button>
            <div className="text-xs text-[#A8A29E]">Times shown in {tz}</div>
          </div>
          <h2 className="font-display font-bold text-xl tracking-tight mb-1">{apt.name}</h2>
          <p className="text-sm text-[#57534E] mb-4">{apt.duration_minutes} min · {LOC_LABEL[apt.location_type]}</p>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_260px] gap-6">
            <div>
              <div className="flex items-center justify-between mb-3">
                <button onClick={() => { const d = new Date(monthAnchor); d.setMonth(d.getMonth() - 1); setMonthAnchor(d); }}
                        className="p-1.5 rounded-lg hover:bg-[#F5F5F4]" data-testid="pub-prev-month">
                  <ChevronLeft size={16} />
                </button>
                <div className="font-medium text-sm">{monthAnchor.toLocaleDateString([], { month: "long", year: "numeric" })}</div>
                <button onClick={() => { const d = new Date(monthAnchor); d.setMonth(d.getMonth() + 1); setMonthAnchor(d); }}
                        className="p-1.5 rounded-lg hover:bg-[#F5F5F4]" data-testid="pub-next-month">
                  <ChevronRight size={16} />
                </button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-[#A8A29E] uppercase mb-1">
                {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => <div key={d}>{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {daysInMonth.map((d, i) => {
                  if (!d) return <div key={`blank-${i}`} />;
                  const key = d.toISOString().slice(0,10);
                  const has = (daysMap[key] || []).length > 0;
                  const isPast = d < new Date(new Date().toDateString());
                  const isSel = selectedDate === key;
                  return (
                    <button key={key} disabled={!has || isPast}
                      onClick={() => { setSelectedDate(key); setSelectedSlot(null); track("date_selected", { org_slug: orgSlug, appointment_type_slug: apt?.slug, user_slug: userSlug }); }}
                      data-testid={`pub-day-${key}`}
                      className={`aspect-square rounded-lg text-sm ${isSel ? "bg-[#E8712C] text-white" :
                        has && !isPast ? "bg-[#FDE9D8] hover:bg-[#F5C9A6] text-[#1C1917]" :
                        "text-[#D6D3D1] cursor-not-allowed"}`}>
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-[#57534E] mb-2">
                {selectedDate ? new Date(selectedDate).toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" }) : "Pick a date"}
              </div>
              <div className="space-y-1.5 max-h-[360px] overflow-y-auto">
                {slotsForDate.length === 0 && selectedDate && <p className="text-xs text-[#A8A29E]">No times available.</p>}
                {slotsForDate.map((s) => (
                  <button key={s.starts_at} onClick={() => { setSelectedSlot(s); setStep(3); track("time_selected", { org_slug: orgSlug, appointment_type_slug: apt?.slug, user_slug: userSlug }); track("started", { org_slug: orgSlug, appointment_type_slug: apt?.slug, user_slug: userSlug }); }}
                          data-testid={`pub-slot-${s.starts_at}`}
                          className="w-full py-2 px-3 rounded-lg border border-[#E8E2D9] text-sm hover:border-[#E8712C] hover:bg-[#FDE9D8] transition-colors">
                    {new Date(s.starts_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: details */}
      {step === 3 && apt && selectedSlot && (
        <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 md:p-8">
          <button onClick={() => setStep(2)} className="text-sm text-[#57534E] flex items-center gap-1 mb-3">
            <ChevronLeft size={14} /> Back
          </button>
          <h2 className="font-display font-bold text-xl tracking-tight mb-1">Your details</h2>
          <p className="text-sm text-[#57534E] mb-4">
            <b>{new Date(selectedSlot.starts_at).toLocaleString([], { weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</b> ({tz})
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>First name *</Label>
              <Input value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})}
                     className="mt-1.5" data-testid="pub-first" /></div>
            <div><Label>Last name *</Label>
              <Input value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})}
                     className="mt-1.5" data-testid="pub-last" /></div>
            <div><Label>Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                     className="mt-1.5" data-testid="pub-email" /></div>
            <div><Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
                     className="mt-1.5" data-testid="pub-phone" /></div>
          </div>
          {(apt.custom_questions || []).map((q) => {
            const key = q.key || q.label;
            const val = answers[key];
            const set = (v) => setAnswers({...answers, [key]: v});
            const label = <Label>{q.label}{q.required ? " *" : ""}</Label>;
            if (q.type === "long_text") return (
              <div key={key} className="mt-3">{label}
                <Textarea rows={3} value={val || ""} onChange={(e) => set(e.target.value)}
                          className="mt-1.5" data-testid={`pub-q-${key}`} /></div>);
            if (q.type === "dropdown") return (
              <div key={key} className="mt-3">{label}
                <select value={val || ""} onChange={(e) => set(e.target.value)} data-testid={`pub-q-${key}`}
                        className="mt-1.5 w-full h-10 rounded-md border border-[#E8E2D9] bg-white px-3 text-sm">
                  <option value="">Select…</option>
                  {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select></div>);
            if (q.type === "multiple_choice") return (
              <div key={key} className="mt-3">{label}
                <div className="mt-1.5 space-y-1">
                  {(q.options || []).map((o) => (
                    <label key={o} className="flex items-center gap-2 text-sm">
                      <input type="radio" name={key} checked={val === o} onChange={() => set(o)}
                             data-testid={`pub-q-${key}-${o}`} /> {o}
                    </label>
                  ))}
                </div></div>);
            if (q.type === "checkbox") return (
              <div key={key} className="mt-3">{label}
                <div className="mt-1.5 space-y-1">
                  {(q.options || []).map((o) => {
                    const arr = Array.isArray(val) ? val : [];
                    const on = arr.includes(o);
                    return (
                      <label key={o} className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={on}
                               onChange={(e) => set(e.target.checked ? [...arr, o] : arr.filter((x) => x !== o))}
                               data-testid={`pub-q-${key}-${o}`} /> {o}
                      </label>
                    );
                  })}
                </div></div>);
            if (q.type === "yes_no") return (
              <div key={key} className="mt-3">{label}
                <div className="mt-1.5 flex gap-3">
                  {["Yes","No"].map((o) => (
                    <label key={o} className="flex items-center gap-1.5 text-sm">
                      <input type="radio" name={key} checked={val === o} onChange={() => set(o)}
                             data-testid={`pub-q-${key}-${o.toLowerCase()}`} /> {o}
                    </label>
                  ))}
                </div></div>);
            return (
              <div key={key} className="mt-3">{label}
                <Input value={val || ""} onChange={(e) => set(e.target.value)}
                       className="mt-1.5" data-testid={`pub-q-${key}`} /></div>);
          })}
          <div className="mt-3">
            <Label>Notes (optional)</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}
                      className="mt-1.5" data-testid="pub-notes" />
          </div>
          <div className="mt-5 flex justify-end">
            <Button onClick={submit} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white" data-testid="pub-submit">
              Confirm booking
            </Button>
          </div>
        </div>
      )}
    </PubShell>
  );
}

function PubShell({ children, org }) {
  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="booking-public-page">
      <header className="bg-[#2C3A29] text-[#FAF8F5]">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <Logo variant="full" size={36} onDark />
          <div className="text-xs text-[#A8A29E]">{org?.name ? `Book with ${org.name}` : "Book an appointment"}</div>
        </div>
      </header>
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 space-y-6">{children}</div>
      <p className="text-center text-xs text-[#A8A29E] py-4">Powered by ClientDesk</p>
    </div>
  );
}

function ErrorCard({ message }) {
  return (
    <div className="bg-white rounded-3xl border border-[#E8E2D9] p-8 text-center">
      <CircleX size={32} className="mx-auto mb-3 text-[#B02222]" strokeWidth={1.5} />
      <h2 className="font-display font-bold text-xl tracking-tight mb-2">This page isn't available</h2>
      <p className="text-sm text-[#57534E]">{message || "Please check the link with the provider."}</p>
    </div>
  );
}
